// src/components/AddDoctor/AddDoctor.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AddDoctor.css";
import drIsInLogo from "../../assets/dr-is-in.png";

type CreateDoctorResponse = any;

const SPECIALTY_OPTIONS = [
  "School Counselor",
  "Psychologist",
  "Psychiatrist",
  "Nurse Practitioner",
  "Social Worker",
  "Licensed Counselor",
  "Behavioral Health",
  "Marriage & Family Therapist",
  "Peer Support",
  "General Medicine",
  "Pediatrics",
  "Child Psychology",
  "Crisis Intervention",
  "Family Medicine",
  "Internal Medicine",
  "Physician Assistant",
  "Crisis Intervention Specialist",
  "Other",
];

const COUNTRY_OPTIONS = ["United States", "Canada", "United Kingdom", "Australia", "India", "Other"];

const STATUS_OPTIONS = ["Active", "On Call", "Inactive", "Retired", "Unavailable"];

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");

function adminToken() {
  return clean(
    localStorage.getItem("admin_token") ||
      localStorage.getItem("admin_access_token") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      ""
  ).replace(/^Bearer\s+/i, "");
}

function clean(v: string) {
  const s = (v || "").trim();
  if (!s) return "";
  if (s.toLowerCase() === "string") return "";
  return s;
}

const AddDoctor: React.FC = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string>("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [specialties, setSpecialties] = useState("School Counselor");
  const [otherSpecialty, setOtherSpecialty] = useState("");
  const [country, setCountry] = useState("United States");
  const [stateVal, setStateVal] = useState("");

  const [website, setWebsite] = useState("");
  const [license, setLicense] = useState("");
  const [bio, setBio] = useState("");
  const [status, setStatus] = useState("Active");

  const [photo, setPhoto] = useState<File | null>(null);

  const handleSubmit = async () => {
    try {
      setErr("");

      if (!clean(name)) return setErr("Name is required.");
      if (!clean(email)) return setErr("Email is required.");

      setSaving(true);

      const fd = new FormData();
      fd.append("name", clean(name));
      fd.append("email", clean(email));
      const finalSpecialty = specialties === "Other" ? clean(otherSpecialty) : clean(specialties);
      if (!finalSpecialty) return setErr("Specialty / role is required.");

      fd.append("specialties", finalSpecialty);
      fd.append("country", clean(country));
      fd.append("state", clean(stateVal));
      fd.append("website", clean(website));
      fd.append("medical_license_number", clean(license));
      fd.append("bio", clean(bio));
      fd.append("status", clean(status));
      fd.append("on_call", status === "On Call" ? "true" : "false");
      fd.append("retired", status === "Retired" ? "true" : "false");
      fd.append("is_active", ["Active", "On Call"].includes(status) ? "true" : "false");
      fd.append("is_approved", "true");
      fd.append("approval_status", "approved");

      if (photo) fd.append("photo", photo);

      const token = adminToken();
      const res = await fetch(`${API_BASE}/admin/doctors`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      const payload: CreateDoctorResponse = await res.json().catch(() => null);

      if (!res.ok) {
        throw { response: { status: res.status, data: payload }, message: payload?.detail || payload?.message || `Failed to add advisor (${res.status}).` };
      }

      navigate("/admin/doctors");
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.message;

      if (status === 404 || status === 405) {
        setErr("Add Advisor endpoint is missing on backend (expected POST /admin/doctors). We need to add it in main.py.");
        return;
      }

      setErr(detail || e?.message || "Failed to add advisor.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="adddoc-wrap">
      <div className="adddoc-card">
        <div className="adddoc-header">
          <div className="adddoc-brand">
            <img className="adddoc-logo" src={drIsInLogo} alt="Doctor Is In" />
            <div>
              <div className="adddoc-title">Add Advisor</div>
              <div className="adddoc-tagline">AI Wellness Advisor with a human touch.</div>
            </div>
          </div>
        </div>

        {err ? <div className="adddoc-error">{err}</div> : null}

        <div className="adddoc-form">
          <input
            className="adddoc-input"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="adddoc-input"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <select className="adddoc-input" value={specialties} onChange={(e) => setSpecialties(e.target.value)}>
            {SPECIALTY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          {specialties === "Other" ? (
            <input
              className="adddoc-input"
              placeholder="Enter advisor specialty / role"
              value={otherSpecialty}
              onChange={(e) => setOtherSpecialty(e.target.value)}
            />
          ) : null}

          <select className="adddoc-input" value={country} onChange={(e) => setCountry(e.target.value)}>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <input
            className="adddoc-input"
            placeholder="State (optional)"
            value={stateVal}
            onChange={(e) => setStateVal(e.target.value)}
          />
          <input
            className="adddoc-input"
            placeholder="Website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
          <input
            className="adddoc-input"
            placeholder="Professional License / Certification #"
            value={license}
            onChange={(e) => setLicense(e.target.value)}
          />

          <textarea
            className="adddoc-textarea adddoc-span-2"
            placeholder="Bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />

          <select className="adddoc-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <div className="adddoc-file adddoc-span-2">
            <label className="adddoc-file-label">Upload Photo</label>
            <input
              className="adddoc-file-input"
              type="file"
              accept="image/*"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)}
            />
          </div>

          <div className="adddoc-btnrow adddoc-span-2">
            <button className="adddoc-submit" type="button" onClick={handleSubmit} disabled={saving}>
              {saving ? "Adding..." : "Add Advisor"}
            </button>

            <button className="adddoc-cancel" type="button" onClick={() => navigate("/dashboard")} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>

        <div className="adddoc-footer">© {year} Doctor Is In App. All rights reserved.</div>
      </div>
    </div>
  );
};

export default AddDoctor;